import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Mail, CheckCircle, Wine, Sparkles, Compass, Award } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import SEO from '../components/SEO';
import FormattedText from '../components/FormattedText';

const getOptimizedImgUrl = (url, width = 800) => {
  if (url && url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  }
  return url;
};

export default function Home() {
  const { t, getRouteUrl } = useLanguage();
  const { hero, aboutSection, services, experience, contactSection } = t.home;
  const [formStatus, setFormStatus] = useState('idle');

  const fadeIn = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }
  };

  const handleHomeFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <main className="bg-[#faf8f5] overflow-x-hidden font-sans pt-24">
      <SEO pageKey="home" />
      
      {/* SECTION 1: HERO (Warm Light Wine Style) */}
      <section className="relative min-h-[90vh] flex items-center bg-[#f7f2ea] text-stone-900 overflow-hidden border-b border-stone-200/60">
        {/* Background Image Masked - Left Side Only */}
        <div className="absolute inset-0 z-0 flex">
          <motion.div 
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.25, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="w-full lg:w-2/3 h-full relative"
          >
            <img 
              src={hero.image} 
              alt="Eliana Wine Hero" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#f7f2ea]/60 to-[#f7f2ea]" />
          </motion.div>
          <div className="hidden lg:block lg:w-1/3 h-full bg-[#f7f2ea]" />
        </div>
        
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-20">
          <div className="flex flex-col lg:items-end lg:text-right">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="w-full lg:max-w-5xl"
            >
              <h1 className="text-4xl md:text-6xl lg:text-[6.5rem] font-serif font-bold leading-none tracking-tight uppercase mb-8 text-stone-950">
                {hero.titleLine1} <br />
                <span className="text-[#581c24] italic font-light">{hero.titleLine2}</span>
              </h1>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-full lg:max-w-4xl ml-auto space-y-8 text-right"
            >
              <div className="flex justify-end items-center gap-4">
                <div className="h-[2px] w-12 bg-[#581c24]"></div>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#581c24]">
                  {hero.badge}
                </span>
              </div>
              <p className="text-base md:text-lg text-stone-700 leading-relaxed font-normal uppercase tracking-wider">
                {hero.description}
              </p>
              <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pt-4">
                <Link to={getRouteUrl('tastings')} className="btn-primary">
                  {hero.ctaTastings}
                </Link>
                <Link to={getRouteUrl('lectures')} className="btn-secondary">
                  {hero.ctaLectures}
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: OM MIG / ABOUT ME */}
      {aboutSection && (
        <motion.section 
          {...fadeIn}
          className="py-20 md:py-32 bg-[#faf8f5] border-b border-stone-200/80"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Left Column: Clean & Engaging Story */}
              <div className="lg:col-span-7 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="h-[2px] w-8 bg-[#581c24]" />
                  <span className="text-xs font-bold uppercase tracking-[0.35em] text-[#581c24]">
                    {aboutSection.badge}
                  </span>
                </div>

                <div className="space-y-2">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-stone-950 leading-tight">
                    {aboutSection.quote}
                  </h2>
                  <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[#581c24]">
                    {aboutSection.title} · {aboutSection.subtitle}
                  </p>
                </div>

                {/* Flowing Story Text */}
                <div className="space-y-4 text-stone-700 text-base md:text-lg leading-relaxed">
                  {aboutSection.lead && (
                    <p className="font-medium text-stone-900">
                      <FormattedText text={aboutSection.lead} />
                    </p>
                  )}
                  {aboutSection.p1 && (
                    <p>
                      <FormattedText text={aboutSection.p1} />
                    </p>
                  )}
                  {aboutSection.p2 && (
                    <p>
                      <FormattedText text={aboutSection.p2} />
                    </p>
                  )}
                  {aboutSection.today && (
                    <p>
                      <FormattedText text={aboutSection.today} />
                    </p>
                  )}
                </div>

                {/* Subtle Philosophy Quote */}
                {aboutSection.philosophy && (
                  <blockquote className="border-l-2 border-[#581c24] pl-4 py-1 my-3">
                    <p className="text-stone-800 font-serif italic text-base md:text-lg leading-relaxed">
                      "{aboutSection.philosophy}"
                    </p>
                  </blockquote>
                )}

                {/* Closing Highlight */}
                {aboutSection.closing && (
                  <p className="text-stone-900 font-bold text-base md:text-lg leading-relaxed">
                    <FormattedText text={aboutSection.closing} />
                  </p>
                )}

                {/* Clean CTA Button */}
                <div className="pt-2">
                  <Link 
                    to={getRouteUrl('about')} 
                    className="btn-primary inline-flex items-center gap-3 group"
                  >
                    <span>{aboutSection.cta}</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>

              {/* Right Column: Clean Portrait */}
              <div className="lg:col-span-5 relative">
                <div className="relative mx-auto max-w-md lg:max-w-none">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-stone-200 bg-stone-100">
                    <img 
                      src={getOptimizedImgUrl(aboutSection.image, 900)} 
                      alt="Eliana Napoli Sommelier" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Single Clean Experience Badge */}
                  <div className="absolute -bottom-5 -left-5 bg-white text-stone-950 p-4 sm:p-5 rounded-xl shadow-lg border border-stone-200 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-lg bg-[#581c24] text-white flex items-center justify-center flex-shrink-0">
                      <Wine className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-xl sm:text-2xl font-serif font-bold italic text-[#581c24] block leading-none mb-0.5">
                        20+
                      </span>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-stone-600 leading-tight">
                        {aboutSection.experienceBadge}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </motion.section>
      )}

      {/* SECTION 3: SERVICES (Vinsmagninger & Vinforedrag) */}
      <section className="pb-24 lg:pb-36 bg-[#f4efe6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          
          {/* Service Block 1: Tastings */}
          <motion.div 
            {...fadeIn}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center mb-24 lg:mb-32 pt-24"
          >
            <div className="lg:col-span-6 flex flex-col justify-center">
              <div className="mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24]">
                  {services.tastings.badge}
                </span>
              </div>
              <h3 className="text-4xl md:text-5xl font-serif font-bold uppercase leading-tight mb-6 text-stone-950">
                {services.tastings.title}
              </h3>
              <p className="text-stone-700 leading-relaxed mb-8 text-base md:text-lg">
                {services.tastings.description}
              </p>
              
              {/* Callout Box */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-[#fcfaf7] text-stone-900 p-8 md:p-10 mb-8 rounded-xl border border-stone-300/80 shadow-md"
              >
                <span className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#581c24] block mb-3">
                  {services.tastings.quoteBadge}
                </span>
                <blockquote className="text-xl md:text-2xl font-serif italic text-stone-800 leading-snug">
                  "{services.tastings.quote}"
                </blockquote>
              </motion.div>

              <Link to={getRouteUrl('tastings')} className="group flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#581c24]">
                {services.tastings.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </div>
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] bg-stone-200 overflow-hidden rounded-xl shadow-lg border border-stone-200">
                <motion.img 
                  initial={{ scale: 1.05 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1 }}
                  src={services.tastings.image} 
                  alt={services.tastings.title} 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Service Block 2: Lectures */}
          <motion.div 
            {...fadeIn}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
          >
            <div className="lg:col-span-6 lg:order-2 flex flex-col justify-center">
              <div className="mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24]">
                  {services.lectures.badge}
                </span>
              </div>
              <h3 className="text-4xl md:text-5xl font-serif font-bold uppercase leading-tight mb-6 text-stone-950">
                {services.lectures.title}
              </h3>
              <p className="text-stone-700 leading-relaxed mb-8 text-base md:text-lg">
                {services.lectures.description}
              </p>

              {/* Callout Box */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-[#fcfaf7] text-stone-900 p-8 md:p-10 mb-8 rounded-xl border border-stone-300/80 shadow-md"
              >
                <span className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#581c24] block mb-3">
                  {services.lectures.quoteBadge}
                </span>
                <blockquote className="text-xl md:text-2xl font-serif italic text-stone-800 leading-snug">
                  "{services.lectures.quote}"
                </blockquote>
              </motion.div>

              <Link to={getRouteUrl('lectures')} className="group flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#581c24]">
                {services.lectures.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
              </Link>
            </div>
            <div className="lg:col-span-6 lg:order-1">
              <div className="aspect-[4/3] bg-stone-200 overflow-hidden rounded-xl shadow-lg border border-stone-200">
                <motion.img 
                  initial={{ scale: 1.05 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1 }}
                  src={services.lectures.image} 
                  alt={services.lectures.title} 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* SECTION 4: MISSION & IMPACT */}
      <motion.section 
        {...fadeIn}
        className="py-24 lg:py-36 bg-[#e8e0d2] text-stone-900 border-t border-b border-stone-300/80"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            
            <div className="lg:col-span-6 space-y-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-[2px] w-8 bg-[#581c24]"></div>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#581c24]">
                  {experience.badge}
                </span>
              </div>
              <h2 className="text-4xl md:text-6xl font-serif font-bold uppercase leading-tight tracking-tight text-stone-950">
                {experience.titleLine1} <br /> 
                <span className="text-[#581c24] italic font-light">{experience.titleLine2}</span>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4">
                {experience.items.map((item, idx) => (
                  <motion.div 
                    key={idx} 
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.08 }}
                    className="space-y-2.5 bg-white/90 p-6 rounded-xl border border-stone-300/80 shadow-xs hover:shadow-md hover:border-[#581c24]/30 hover:bg-white transition-all"
                  >
                    <h4 className="text-base sm:text-lg font-serif font-bold text-[#581c24]">{item.title}</h4>
                    <p className="text-sm sm:text-[15px] text-stone-800 leading-relaxed font-normal">{item.text}</p>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-8 pt-4">
                <div className="space-y-1">
                  <span className="text-3xl font-serif font-bold text-[#581c24] block">{experience.stat1Number}</span>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-700 font-bold">{experience.stat1Label}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-3xl font-serif font-bold text-[#581c24] block">{experience.stat2Number}</span>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-700 font-bold">{experience.stat2Label}</p>
                </div>
              </div>

              <Link to={getRouteUrl('contact')} className="btn-primary mt-6">
                {experience.cta} <ArrowRight className="inline-block ml-2 w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-4 h-full">
                <div className="space-y-4 h-full flex flex-col">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="flex-1 bg-stone-200 overflow-hidden rounded-xl shadow-md min-h-[220px]"
                  >
                    <img src="https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/4b48daac-7874-4af2-bb70-1c8c51ac136e.jpg" alt="Vineyard" className="w-full h-full object-cover" />
                  </motion.div>
                </div>
                <div className="space-y-4 h-full flex flex-col pt-12">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex-1 bg-stone-200 overflow-hidden rounded-xl shadow-md min-h-[220px]"
                  >
                    <img src="https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg" alt="Wine Pour" className="w-full h-full object-cover" />
                  </motion.div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </motion.section>

      {/* SECTION 5: CONTACT / LEAD GEN */}
      <motion.section 
        {...fadeIn}
        className="py-24 lg:py-36 bg-[#faf6f0] border-t border-stone-200/80"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            
            <div className="lg:col-span-6 space-y-8">
              <h2 className="text-4xl md:text-6xl font-serif font-bold uppercase leading-tight tracking-tight text-stone-950">
                {contactSection.titleLine1} <br /> 
                <span className="text-[#581c24] italic font-light">{contactSection.titleLine2}</span>
              </h2>
              <p className="text-stone-700 leading-relaxed max-w-md">
                {contactSection.subtitle}
              </p>
              
              <div className="space-y-6 pt-4">
                <div className="flex items-center gap-6 group">
                  <div className="w-12 h-12 bg-[#f2ede4] rounded-xl flex items-center justify-center text-[#581c24]">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold mb-1">
                      {t.common.email}
                    </p>
                    <a href={`mailto:${t.contactInfo.email}`} className="text-stone-900 font-medium hover:text-[#581c24] transition-colors">
                      {t.contactInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-6 group">
                  <div className="w-12 h-12 bg-[#f2ede4] rounded-xl flex items-center justify-center text-[#581c24]">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold mb-1">
                      {t.common.phone}
                    </p>
                    <a href={`tel:${t.contactInfo.phone}`} className="text-stone-900 font-medium hover:text-[#581c24] transition-colors">
                      {t.contactInfo.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-8 md:p-12 rounded-xl shadow-lg border border-stone-200"
              >
                {formStatus === 'success' ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-stone-900">
                      {t.common.thankYouMessage}
                    </h3>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="btn-secondary mt-4 text-xs"
                    >
                      {t.common.sendInquiry}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleHomeFormSubmit} className="space-y-6">
                    {/* Antispam Honeypot */}
                    <div className="sr-only absolute -z-50 opacity-0 pointer-events-none">
                      <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                          {t.common.name}
                        </label>
                        <input type="text" name="name" className="w-full border-b border-stone-300 py-2 focus:border-[#581c24] outline-none transition-colors" required />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                          {t.common.email}
                        </label>
                        <input type="email" name="email" className="w-full border-b border-stone-300 py-2 focus:border-[#581c24] outline-none transition-colors" required />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                        {t.common.subject}
                      </label>
                      <select name="subject" className="w-full border-b border-stone-300 py-2 focus:border-[#581c24] outline-none transition-colors bg-transparent">
                        {contactSection.formSubjectOptions.map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                        {t.common.message}
                      </label>
                      <textarea name="message" rows="4" className="w-full border-b border-stone-300 py-2 focus:border-[#581c24] outline-none transition-colors resize-none" required></textarea>
                    </div>

                    {formStatus === 'error' && (
                      <p className="text-xs text-rose-700 font-medium">
                        {t.common.errorMessage}
                      </p>
                    )}

                    <button 
                      type="submit" 
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-[#581c24] text-white py-4 rounded-[10px] text-xs font-bold uppercase tracking-[0.3em] hover:bg-[#42141a] transition-colors shadow-sm disabled:opacity-50"
                    >
                      {formStatus === 'submitting' ? t.common.sending : t.common.sendInquiry}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>

          </div>
        </div>
      </motion.section>

    </main>
  );
}
